import { Bell, Search, Menu, X, Clock, FileText, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { auth, db } from '../../../firebase';
import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, getDocs, query, orderBy, limit, doc, updateDoc, arrayUnion, deleteDoc, writeBatch, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const normalize = (value) => value?.toString().toLowerCase() || '';

const matchesQuery = (fields, query) => fields.some((field) => normalize(field).includes(query));

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

const formatStatus = (status) => {
  if (!status) return 'Pending';
  const pretty = status.replace('-', ' ');
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
};

export default function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(() => auth.currentUser);
  const userName = currentUser?.displayName || 'Admin';
  const firstName = userName.split(' ')[0];
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchData, setSearchData] = useState({
    personalLoans: [],
    businessLoans: [],
    customers: []
  });
  const [searchDataLoading, setSearchDataLoading] = useState(true);
  const [searchProcessing, setSearchProcessing] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const searchContainerRef = useRef(null);
  const notificationsRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!currentUser?.uid) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    try {
      const notificationsRef = collection(db, 'notifications');
      const notificationsQuery = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(notificationsQuery);
      const now = new Date();
      const expiredNotificationIds = [];

      const items = [];
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() || {};
        const createdAt = data.createdAt?.toDate
          ? data.createdAt.toDate()
          : data.createdAt
          ? new Date(data.createdAt)
          : new Date();
        const readBy = Array.isArray(data.readBy) ? data.readBy : [];
        const readAt = data.readAt || {};
        const isRead = readBy.includes(currentUser.uid);

        // Check if notification should be auto-deleted (read + 24 hours old)
        if (isRead && readAt[currentUser.uid]) {
          try {
            const userReadTime = readAt[currentUser.uid].toDate 
              ? readAt[currentUser.uid].toDate() 
              : new Date(readAt[currentUser.uid]);
            const hoursSinceRead = (now - userReadTime) / (1000 * 60 * 60);
            
            if (hoursSinceRead >= 24) {
              // Mark for deletion
              expiredNotificationIds.push(docSnap.id);
              continue; // Skip adding to items
            }
          } catch (timeError) {
            // If there's an error parsing the read time, keep the notification
          }
        }

        items.push({
          id: docSnap.id,
          title: data.title || 'New update',
          message: data.message || data.body || 'Tap to view details.',
          type: data.type || 'info',
          link: data.link || data.url || null,
          time: createdAt,
          isNew: !isRead,
        });
      }

      setNotifications(items);
      setUnreadCount(items.filter((item) => item.isNew).length);

      // Delete expired notifications in the background (non-blocking)
      if (expiredNotificationIds.length > 0) {
        // Run deletion asynchronously without waiting
        const deleteExpired = async () => {
          try {
            const batch = writeBatch(db);
            expiredNotificationIds.forEach((id) => {
              batch.delete(doc(db, 'notifications', id));
            });
            await batch.commit();
          } catch (deleteError) {
            // Silent fail for background deletion
          }
        };
        deleteExpired();
      }
    } catch (error) {
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 5 minutes
    const interval = setInterval(fetchNotifications, 300000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const loadSearchIndex = async () => {
      try {
        const [personalSnapshot, businessSnapshot, usersSnapshot] = await Promise.all([
          getDocs(collection(db, 'personalLoan')),
          getDocs(collection(db, 'businessLoans')),
          getDocs(collection(db, 'users'))
        ]);

        const personalLoans = personalSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            fullName: data.fullName || '',
            email: data.email || '',
            mobileNumber: data.mobileNumber || '',
            aadharNumber: data.aadharNumber || '',
            panNumber: data.panNumber || '',
            loanAmount: data.loanAmount || 0,
            status: data.status || 'pending'
          };
        });

        const businessLoans = businessSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessName: data.businessName || '',
            ownerName: data.ownerName || '',
            email: data.email || '',
            mobileNumber: data.mobileNumber || '',
            gstNumber: data.gstNumber || '',
            panNumber: data.panNumber || '',
            loanAmount: data.loanAmount || 0,
            status: data.status || 'pending'
          };
        });

        const customers = usersSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || '',
            email: data.email || '',
            phoneNumber: data.phoneNumber || '',
            uid: data.uid || ''
          };
        });

        setSearchData({ personalLoans, businessLoans, customers });
      } catch (error) {
        // Silent fail
      } finally {
        setSearchDataLoading(false);
      }
    };

    loadSearchIndex();
  }, []);

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'warning':
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'reminder':
      case 'schedule':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'document':
      case 'loan':
        return <FileText className="w-5 h-5 text-purple-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const handleNotificationClick = useCallback(
    async (notification, event) => {
      // Check if delete button was clicked
      if (event?.target?.closest('.delete-notification-btn')) {
        return;
      }

      if (!notification) {
        return;
      }

      if (notification.id && notification.isNew && currentUser?.uid) {
        try {
          const notificationDoc = doc(db, 'notifications', notification.id);
          await updateDoc(notificationDoc, {
            readBy: arrayUnion(currentUser.uid),
            [`readAt.${currentUser.uid}`]: new Date(),
          });
        } catch (error) {
          // Silent fail
        }
      }

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notification.id ? { ...item, isNew: false } : item
        )
      );

      setUnreadCount((prev) =>
        notification.isNew ? Math.max(0, prev - 1) : prev
      );

      setShowNotifications(false);

      if (notification.link) {
        navigate(notification.link);
      }
    },
    [currentUser?.uid, navigate]
  );

  const handleDeleteNotification = useCallback(
    async (notificationId, event) => {
      event.stopPropagation();
      
      try {
        await deleteDoc(doc(db, 'notifications', notificationId));
        
        setNotifications((prev) => {
          const notification = prev.find(n => n.id === notificationId);
          if (notification?.isNew) {
            setUnreadCount((count) => Math.max(0, count - 1));
          }
          return prev.filter((item) => item.id !== notificationId);
        });
        
        toast.success('Notification deleted');
      } catch (error) {
        toast.error('Failed to delete notification');
      }
    },
    []
  );

  const handleClearAllNotifications = useCallback(async () => {
    if (notifications.length === 0) return;
    
    try {
      const batch = writeBatch(db);
      notifications.forEach((notification) => {
        batch.delete(doc(db, 'notifications', notification.id));
      });
      
      await batch.commit();
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  }, [notifications]);

  const buildSearchResults = useCallback(
    (rawQuery) => {
      const queryText = normalize(rawQuery);
      if (!queryText) {
        return [];
      }

      const results = [];

      searchData.personalLoans.forEach((loan) => {
        const fields = [
          loan.fullName,
          loan.email,
          loan.mobileNumber,
          loan.aadharNumber,
          loan.panNumber,
        ];

        if (matchesQuery(fields, queryText)) {
          results.push({
            id: loan.id,
            type: 'Personal Loan',
            icon: 'personal',
            title: loan.fullName || 'Personal loan applicant',
            subtitle: `${formatCurrency(loan.loanAmount)} • ${formatStatus(loan.status)}`,
            route: `/personal-loans/${loan.id}`,
          });
        }
      });

      searchData.businessLoans.forEach((loan) => {
        const fields = [
          loan.businessName,
          loan.ownerName,
          loan.email,
          loan.mobileNumber,
          loan.gstNumber,
          loan.panNumber,
        ];

        if (matchesQuery(fields, queryText)) {
          results.push({
            id: loan.id,
            type: 'Business Loan',
            icon: 'business',
            title: loan.businessName || loan.ownerName || 'Business loan applicant',
            subtitle: `${formatCurrency(loan.loanAmount)} • ${formatStatus(loan.status)}`,
            route: `/business-loans/${loan.id}`,
          });
        }
      });

      searchData.customers.forEach((customer) => {
        const fields = [
          customer.name,
          customer.email,
          customer.phoneNumber,
          customer.uid,
        ];

        if (matchesQuery(fields, queryText)) {
          results.push({
            id: customer.id,
            type: 'Customer',
            icon: 'customer',
            title: customer.name || 'Customer record',
            subtitle: customer.email || customer.phoneNumber || 'View customer profile',
            route: `/customers/${customer.id}`,
          });
        }
      });

      return results.slice(0, 25);
    },
    [searchData]
  );

  useEffect(() => {
    if (!showSearch && !showMobileSearch) {
      setSearchResults([]);
      setSearchProcessing(false);
      return;
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearchProcessing(false);
      return;
    }

    if (searchDataLoading) {
      setSearchProcessing(false);
      return;
    }

    setSearchProcessing(true);
    const timer = setTimeout(() => {
      const results = buildSearchResults(searchQuery);
      setSearchResults(results);
      setSearchProcessing(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery, showSearch, showMobileSearch, searchDataLoading, buildSearchResults]);

  const groupedSearchResults = searchResults.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {});

  const handleSearchResultClick = (result) => {
    navigate(result.route);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  const getSearchIcon = (iconType) => {
    switch (iconType) {
      case 'personal':
        return <FileText className="w-5 h-5 text-green-600" />;
      case 'business':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'customer':
        return <FileText className="w-5 h-5 text-purple-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearch && searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearch(false);
      }
      if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSearch, showNotifications]);

  // Auto-focus mobile search input when opened
  useEffect(() => {
    if (showMobileSearch && mobileSearchInputRef.current) {
      setTimeout(() => {
        mobileSearchInputRef.current?.focus();
      }, 100);
    }
  }, [showMobileSearch]);

  const handleMobileSearchClick = () => {
    setShowMobileSearch(true);
    setSearchQuery('');
  };

  const closeMobileSearch = () => {
    setShowMobileSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <header className="border-b border-gray-100 sticky top-0 z-30 backdrop-blur-lg bg-white/80">
      <div className="px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="flex items-center justify-between gap-3">
          {/* Left Section - Menu & Welcome */}
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              onClick={onMenuClick}
              className="lg:hidden p-1.5 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>

            {/* Welcome Message */}
            <div className="min-w-0">
              <h1 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 truncate">
                Welcome back,
                <span className="hidden sm:inline"> {firstName}</span>
                <span className="inline sm:hidden"> {firstName.length > 8 ? firstName.slice(0, 8) + '...' : firstName}</span> 👋
              </h1>
              <p className="text-gray-500 text-[11px] sm:text-xs mt-0 hidden md:block">
                Here's what's happening with your loans today.
              </p>
            </div>
          </div>

          {/* Right Section - Search & Notifications */}
          <div className="flex items-center gap-1.5 sm:gap-2.5">
            {/* Search Bar - Hidden on mobile */}
            <div ref={searchContainerRef} className="relative hidden md:block">
              <input
                type="text"
                placeholder="Search loans, customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSearch(true)}
                className="pl-10 pr-4 py-1.5 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48 lg:w-60 transition-all"
              />
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              
              {/* Search Results Dropdown */}
              {showSearch && (searchQuery.length >= 2 || searchResults.length > 0) && (
               
                  <div className="absolute right-0 mt-2 w-80 lg:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-40 max-h-96 overflow-hidden flex flex-col">
                    <div className="px-4 py-3 flex justify-between items-center border-b border-gray-100 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Search Results
                        {searchResults.length > 0 && ` (${searchResults.length})`}
                      </p>
                      <button
                        onClick={() => setShowSearch(false)}
                        className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>

                    <div className="overflow-y-auto flex-1">
                      {searchDataLoading ? (
                        <div className="flex items-center justify-center py-10 px-4">
                          <div className="text-center">
                            <div className="w-7 h-7 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-sm text-gray-500 font-medium">Preparing search index...</p>
                            <p className="text-xs text-gray-400 mt-1">Please wait a moment</p>
                          </div>
                        </div>
                      ) : searchQuery.trim().length < 2 ? (
                        <div className="flex flex-col items-center justify-center py-10 px-4">
                          <Search className="w-10 h-10 text-gray-300 mb-2" />
                          <p className="text-gray-500 text-sm font-medium">Search across loans & customers</p>
                          <p className="text-gray-400 text-xs mt-1">Enter at least 2 characters to begin</p>
                        </div>
                      ) : searchProcessing ? (
                        <div className="flex items-center justify-center py-10 px-4">
                          <div className="text-center">
                            <div className="w-7 h-7 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-sm text-gray-500 font-medium">Searching records...</p>
                            <p className="text-xs text-gray-400 mt-1">Looking through customers and loans</p>
                          </div>
                        </div>
                      ) : searchResults.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 px-4">
                          <Search className="w-10 h-10 text-gray-300 mb-2" />
                          <p className="text-gray-500 text-sm font-medium">No results found</p>
                          <p className="text-gray-400 text-xs mt-1">Try refining your search keywords</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {['Personal Loan', 'Business Loan', 'Customer'].map((group) => (
                            groupedSearchResults[group]?.length ? (
                              <div key={group} className="py-2">
                                <p className="px-4 py-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                                  {group}
                                </p>
                                <div className="divide-y divide-gray-100">
                                  {groupedSearchResults[group].map((result) => (
                                    <button
                                      key={`${result.type}-${result.id}`}
                                      onClick={() => handleSearchResultClick(result)}
                                      className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-start gap-3"
                                    >
                                      <div className="shrink-0 mt-0.5">
                                        {getSearchIcon(result.icon)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm text-gray-900 truncate">
                                          {result.title}
                                        </p>
                                        <p className="text-xs text-gray-600 truncate">
                                          {result.subtitle}
                                        </p>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ) : null
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
              )}
            </div>

            {/* Search Button - Mobile only */}
            <button 
              onClick={handleMobileSearchClick}
              className="md:hidden p-1.5 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>

            {/* Notifications */}
            <div ref={notificationsRef} className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors group"
              >
                <Bell className={`w-5 h-5 transition-colors ${unreadCount > 0 ? 'text-blue-600' : 'text-gray-600'} group-hover:text-gray-900`} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white px-1 animate-pulse">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowNotifications(false)}
                  ></div>

                  {/* Dropdown Panel */}
                  <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-40 max-h-[80vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-gray-900 text-base">Notifications</h3>
                          {unreadCount > 0 && (
                            <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                              <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                              {unreadCount} new notification{unreadCount !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {notifications.length > 0 && (
                            <button
                              onClick={handleClearAllNotifications}
                              className="p-1.5 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                              title="Clear all notifications"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => setShowNotifications(false)}
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Notifications List */}
                    <div className="overflow-y-auto flex-1">
                      {loading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="text-center">
                            <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                            <p className="text-sm text-gray-500">Loading...</p>
                          </div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                          <Bell className="w-12 h-12 text-gray-300 mb-3" />
                          <p className="text-gray-500 text-sm font-medium">No notifications</p>
                          <p className="text-gray-400 text-xs mt-1">You're all caught up!</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {notifications.map((notification) => (
                            <div
                              key={`${notification.id}-${notification.time.getTime()}`}
                              className={`w-full px-4 py-3 hover:bg-gray-50 transition-all flex items-start gap-3 group relative ${
                                notification.isNew ? 'bg-blue-50/30 border-l-4 border-blue-500' : ''
                              }`}
                            >
                              <button
                                onClick={(e) => handleNotificationClick(notification, e)}
                                className="flex-1 flex items-start gap-3 text-left min-w-0"
                              >
                                <div className="shrink-0 mt-0.5">
                                  {getNotificationIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <p className="font-semibold text-sm text-gray-900 line-clamp-1">
                                      {notification.title}
                                    </p>
                                    {notification.isNew && (
                                      <span className="w-2 h-2 bg-blue-600 rounded-full shrink-0 mt-1.5 animate-pulse"></span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 line-clamp-2 mb-1">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {getTimeAgo(notification.time)}
                                  </p>
                                </div>
                              </button>
                              
                              {/* Delete Button */}
                              <button
                                onClick={(e) => handleDeleteNotification(notification.id, e)}
                                className="delete-notification-btn shrink-0 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-100 text-red-600 rounded-lg transition-all"
                                title="Delete notification"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                        <button
                          onClick={() => {
                            fetchNotifications();
                          }}
                          className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          Refresh Notifications
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search Modal */}
      {showMobileSearch && (
        <div className="fixed inset-0 z-50 bg-white md:hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={closeMobileSearch}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
              <div className="relative flex-1">
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  placeholder="Search loans, customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                />
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search Results */}
          <div className="overflow-y-auto h-[calc(100vh-73px)] bg-white">
            {searchDataLoading ? (
              <div className="flex items-center justify-center py-20 px-4 bg-white">
                <div className="text-center">
                  <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600 font-medium">Preparing search...</p>
                  <p className="text-xs text-gray-400 mt-1">Loading data index</p>
                </div>
              </div>
            ) : searchQuery.trim().length < 2 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 bg-white">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-gray-700 text-base font-semibold mb-1">Search Everything</p>
                <p className="text-gray-500 text-sm text-center">
                  Find loans, customers, and applications
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  Enter at least 2 characters to begin
                </p>
              </div>
            ) : searchProcessing ? (
              <div className="flex items-center justify-center py-20 px-4 bg-white">
                <div className="text-center">
                  <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600 font-medium">Searching...</p>
                  <p className="text-xs text-gray-400 mt-1">Looking through records</p>
                </div>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 bg-white">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-700 text-base font-semibold mb-1">No results found</p>
                <p className="text-gray-500 text-sm text-center">
                  Try different keywords or check spelling
                </p>
              </div>
            ) : (
              <div className="pb-6 bg-white">
                {['Personal Loan', 'Business Loan', 'Customer'].map((group) => (
                  groupedSearchResults[group]?.length ? (
                    <div key={group} className="mb-4">
                      <div className="px-4 py-2 bg-gray-50 sticky top-0">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                          {group} ({groupedSearchResults[group].length})
                        </p>
                      </div>
                      <div className="divide-y divide-gray-100 bg-white">
                        {groupedSearchResults[group].map((result) => (
                          <button
                            key={`${result.type}-${result.id}`}
                            onClick={() => {
                              handleSearchResultClick(result);
                              closeMobileSearch();
                            }}
                            className="w-full px-4 py-4 text-left bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-start gap-3"
                          >
                            <div className="shrink-0 mt-1">
                              {getSearchIcon(result.icon)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-gray-900 mb-0.5">
                                {result.title}
                              </p>
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {result.subtitle}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
